// Field definitions with aliases for flexible auto-mapping
// The 'aliases' array contains all common variations that should map to this field
export const mapFields = [
  // Name field with dual-column mapping support
  {
    key: "name",
    label: "Name",
    required: false,
    description: "Full name OR map First Name + Last Name columns",
    hasDualMapping: true, // Special flag to show two dropdowns
    secondaryKey: "lastName",
    primaryLabel: "First Name",
    secondaryLabel: "Last Name",
    aliases: ["name", "fullname", "full_name", "full name", "contact", "contactname", "contact_name", "leadname", "lead_name", "customer", "customername", "customer_name",
      // Book-of-business exports (often "LAST, FIRST" — the backend flips those)
      "client name", "clientname", "client_name", "insured name", "insuredname", "insured_name",
      "primary insured", "policyholder", "policy holder", "policyholder name", "policy holder name"],
  },
  {
    key: "firstName",
    label: "First Name",
    required: false,
    description: "First name of the lead",
    aliases: ["firstname", "first_name", "first name", "fname", "givenname", "given_name", "given name", "forename",
      // GoHighLevel format (contact.first_name normalizes to contactfirstname)
      "contact.first_name", "contact.firstname",
      // Salesforce format (Lead: First Name normalizes to leadfirstname)
      "lead: first name", "lead:first name", "lead:firstname", "contact: first name", "contact:firstname",
      // "Name: First" style (e.g. Clinton Macomb Opt In Database)
      "name: first", "name:first", "name_first"],
  },
  {
    key: "lastName",
    label: "Last Name",
    required: false,
    description: "Last name of the lead",
    aliases: ["lastname", "last_name", "last name", "lname", "surname", "familyname", "family_name", "family name",
      // GoHighLevel format
      "contact.last_name", "contact.lastname",
      // Salesforce format
      "lead: last name", "lead:last name", "lead:lastname", "contact: last name", "contact:lastname",
      // "Name: Last" style
      "name: last", "name:last", "name_last"],
  },
  {
    key: "middleName",
    label: "Middle Name / Initial",
    required: false,
    description: "Placed between the first and last name in the lead's full name",
    aliases: ["mi", "MI", "middle", "middlename", "middle_name", "middle name",
      "middleinitial", "middle_initial", "middle initial", "mname", "m_name"],
  },
  {
    key: "email",
    label: "Email Address",
    required: false,
    description: "Primary email address",
    aliases: ["email", "emailaddress", "email_address", "email address", "e-mail", "mail", "emailid", "email_id", "primaryemail", "primary_email",
      // FollowUpBoss format
      "email 1", "email1", "Email 1", "Email1",
      // GoHighLevel format
      "contact.email",
      // Salesforce format
      "lead: email", "lead:email", "contact: email", "contact:email"],
  },
  {
    key: "phone",
    label: "Phone Number",
    required: false,
    description: "Contact phone number (Phone 1 only - other phones go to custom fields)",
    aliases: ["phone", "telephone", "tel", "phonenumber", "phone_number", "phone number", "mobile", "cell", "cellphone", "cell_phone", "contact_phone", "primaryphone", "primary_phone", "phonenum", "phone_num",
      // FollowUpBoss format - ONLY Phone 1 maps to main phone field
      "phone 1", "phone1", "Phone 1", "Phone1",
      // GoHighLevel format
      "contact.phone",
      // Salesforce format
      "lead: phone", "lead:phone", "contact: phone", "contact:phone", "lead: mobile phone", "lead:mobilephone",
      // Mortgage/property data vendors (LoanVault et al.)
      "cell_num", "cellnum", "cell num", "cellphonenumber", "cell_number", "cell number", "mobilenum", "mobile_num"],
  },
  // Additional phone fields that go to custom fields (Phone 2-6 from FollowUpBoss)
  {
    key: "customFields.phone2",
    label: "Phone 2",
    required: false,
    description: "Secondary phone number (stored as custom field)",
    aliases: ["phone 2", "phone2", "Phone 2", "Phone2"],
  },
  {
    key: "customFields.phone3",
    label: "Phone 3",
    required: false,
    description: "Additional phone number (stored as custom field)",
    aliases: ["phone 3", "phone3", "Phone 3", "Phone3"],
  },
  {
    key: "customFields.phone4",
    label: "Phone 4",
    required: false,
    description: "Additional phone number (stored as custom field)",
    aliases: ["phone 4", "phone4", "Phone 4", "Phone4"],
  },
  {
    key: "customFields.phone5",
    label: "Phone 5",
    required: false,
    description: "Additional phone number (stored as custom field)",
    aliases: ["phone 5", "phone5", "Phone 5", "Phone5"],
  },
  {
    key: "customFields.phone6",
    label: "Phone 6",
    required: false,
    description: "Additional phone number (stored as custom field)",
    aliases: ["phone 6", "phone6", "Phone 6", "Phone6"],
  },
  {
    key: "dob",
    label: "Date of Birth",
    required: false,
    description: "Date of birth (supports MM-DD-YYYY, MM/DD/YYYY, YYYY-MM-DD formats)",
    aliases: ["dob", "dateofbirth", "date_of_birth", "date of birth", "birthday", "birthdate", "birth_date", "birth date", "bday", "born", "birthdt",
      // FollowUpBoss format
      "Birthday", "D.O.B", "Date of Birth "],
  },
  {
    key: "gender",
    label: "Gender",
    required: false,
    description: "Gender of the lead",
    aliases: ["gender", "sex", "m/f", "male/female", "Gender"],
  },
  {
    key: "age",
    label: "Age",
    required: false,
    description: "Age of the lead",
    aliases: ["age", "years", "yearsold", "years_old"],
  },
  {
    key: "height",
    label: "Height",
    required: false,
    description: "Height of the lead",
    aliases: ["height", "ht", "tall", "stature"],
  },
  {
    key: "weight",
    label: "Weight",
    required: false,
    description: "Weight of the lead",
    aliases: ["weight", "wt", "mass", "lbs", "pounds"],
  },

  // Family Information
  {
    key: "marital",
    label: "Marital Status",
    required: false,
    description: "Marital status of the lead",
    aliases: ["marital", "maritalstatus", "marital_status", "marital status", "married", "single", "relationship", "relationshipstatus"],
  },

  // Income Information
  {
    key: "employmentStatus",
    label: "Employment Status",
    required: false,
    description: "Current employment status",
    aliases: ["employmentstatus", "employment_status", "employment status", "employment", "employed", "workstatus", "work_status", "jobstatus", "job_status"],
  },
  {
    key: "earned",
    label: "Annual Income Earned",
    required: false,
    description: "Annual earned income",
    aliases: ["earned", "earnedincome", "earned_income", "annualincome", "annual_income", "annual income", "income", "salary", "wages", "earnings", "yearlyincome", "yearly_income"],
  },
  {
    key: "unearned",
    label: "Annual Unearned Income",
    required: false,
    description: "Annual unearned income",
    aliases: ["unearned", "unearnedincome", "unearned_income", "passiveincome", "passive_income", "otherincome", "other_income"],
  },
  {
    key: "worth",
    label: "Net Worth",
    required: false,
    description: "Estimated net worth",
    aliases: ["worth", "networth", "net_worth", "net worth", "totalworth", "total_worth", "assets", "totalassets"],
  },

  // Lead Management
  {
    key: "assigned",
    label: "Assigned To",
    required: false,
    description: "User or agent assigned to the lead",
    aliases: ["assigned", "assignedto", "assigned_to", "assigned to", "agent", "agentname", "agent_name", "rep", "representative", "salesperson", "sales_person",
      // FollowUpBoss format
      "Assigned To", "Owner", "owner"],
  },
  {
    key: "leadStat",
    label: "Lead Status",
    required: false,
    description: "Current status of the lead",
    aliases: ["leadstat", "lead_stat", "leadstatus", "lead_status", "lead status", "status", "stage", "leadstage", "lead_stage", "disposition",
      // FollowUpBoss format
      "Stage"],
  },

  // Address Information
  {
    key: "address",
    label: "Address",
    required: false,
    description: "Street address of the lead",
    aliases: ["address", "streetaddress", "street_address", "street address", "street", "addr", "address1", "address_1", "addressline1", "address_line_1", "mailingaddress", "mailing_address", "homeaddress", "home_address",
      // GoHighLevel format
      "contact.address1", "contact.address",
      // Salesforce format
      "lead: street", "lead:street", "mailing street", "mailingstreet",
      // FollowUpBoss format - various capitalizations and spacing
      "address 1 - street", "Address 1 - Street", "Address 1 - street", "address1 - street", "Address1 - Street",
      "address1street", "Address1Street",
      "property address", "Property Address", "propertyaddress",
      // Alternative formats
      "street address", "Street Address", "streetaddress1", "Street Address 1",
      // Mortgage/property data vendors ship the street as ADDR1/ADDR2
      "addr1", "ADDR1", "addr_1", "addr 1", "addrline1", "addr line 1"],
  },
  {
    key: "addr2",
    label: "Address Line 2",
    required: false,
    description: "Apt / suite / unit — appended to the street address, not stored separately",
    aliases: ["addr2", "ADDR2", "addr_2", "addr 2", "address2", "address_2", "address 2",
      "addressline2", "address_line_2", "address line 2", "addrline2", "addr line 2",
      // Bare "unit" is deliberately NOT here — in an insurance file it reads as
      // unit-linked / units of coverage, not an apartment.
      "apt", "suite", "aptnumber", "apt_number", "unitnumber", "unit_number"],
  },
  {
    key: "city",
    label: "City",
    required: false,
    description: "City of the lead",
    aliases: ["city", "town", "municipality", "locality", "place",
      // FollowUpBoss format
      "address 1 - city", "Address 1 - City", "property city", "Property City",
      // GoHighLevel format
      "contact.city",
      // Salesforce format
      "lead: city", "lead:city", "mailing city", "mailingcity"],
  },
  {
    key: "state",
    label: "State",
    required: false,
    description: "State (2-letter code)",
    aliases: ["state", "st", "province", "region", "statecode", "state_code", "stateprovince", "state_province",
      // FollowUpBoss format
      "address 1 - state", "Address 1 - State", "property state", "Property State",
      // GoHighLevel format
      "contact.state",
      // Salesforce format
      "lead: state", "lead:state", "mailing state", "mailingstate", "mailing state/province"],
  },
  {
    key: "zipCode",
    label: "Zip Code",
    required: false,
    description: "ZIP/Postal code",
    aliases: ["zipcode", "zip_code", "zip code", "zip", "postalcode", "postal_code", "postal code", "postal", "postcode", "post_code",
      // FollowUpBoss format
      "address 1 - zip", "Address 1 - Zip", "property postal code", "Property Postal Code",
      // GoHighLevel format
      "contact.postalCode", "contact.postal_code", "contact.zip",
      // Salesforce format
      "lead: zip/postal code", "lead:zip", "mailing zip/postal code", "mailingpostalcode", "mailing zip"],
  },

  // Additional Fields
  {
    key: "occupation",
    label: "Occupation",
    required: false,
    description: "Occupation of the lead",
    aliases: ["occupation", "job", "jobtitle", "job_title", "job title", "profession", "work", "career", "position", "title", "role"],
  },
  {
    key: "notes",
    label: "Notes",
    required: false,
    description: "Additional notes about the lead",
    // Notes and note should be prioritized over Message/Description - put exact matches first
    aliases: ["notes", "Notes", "note", "Note", "comments", "comment", "remarks", "remark", "info", "information", "details", "additionalinfo", "additional_info",
      // FollowUpBoss format - lower priority (Message, Description, Background are secondary)
      "Message", "Description", "Background", "description"],
    // Priority aliases that should be preferred when multiple columns match
    priorityAliases: ["notes", "Notes", "note", "Note"],
  },

  // Additional Phone Fields
  {
    key: "phone_home",
    label: "Home Phone",
    required: false,
    description: "Home phone number",
    aliases: ["phone_home", "phonehome", "home_phone", "homephone", "home phone", "hphone", "landline", "home"],
  },
  {
    key: "phone_work",
    label: "Work Phone",
    required: false,
    description: "Work phone number",
    aliases: ["phone_work", "phonework", "work_phone", "workphone", "work phone", "wphone", "office", "officephone", "office_phone", "business", "businessphone", "business_phone"],
  },
  {
    key: "phone_mobile",
    label: "Mobile Phone",
    required: false,
    description: "Mobile phone number",
    aliases: ["phone_mobile", "phonemobile", "mobile_phone", "mobilephone", "mobile phone", "mphone", "cell", "cellphone", "cell_phone", "cellular", "wireless"],
  },

  // Insurance-specific fields
  {
    key: "beneficiary",
    label: "Beneficiary",
    required: false,
    description: "Primary beneficiary name",
    aliases: ["beneficiary", "beneficiaryname", "beneficiary_name", "beneficiary name", "primarybeneficiary", "primary_beneficiary"],
  },
  {
    key: "smoker",
    label: "Smoker Status",
    required: false,
    description: "Whether the lead is a smoker",
    aliases: ["smoker", "smoking", "smokerstatus", "smoker_status", "tobacco", "tobaccouse", "tobacco_use"],
  },
  {
    key: "healthConditions",
    label: "Health Conditions",
    required: false,
    description: "Known health conditions",
    aliases: ["healthconditions", "health_conditions", "health conditions", "conditions", "medicalconditions", "medical_conditions", "health", "medicalhistory", "medical_history"],
  },
  {
    key: "coverageAmount",
    label: "Coverage Amount",
    required: false,
    description: "Desired coverage amount",
    aliases: ["coverageamount", "coverage_amount", "coverage amount", "coverage", "faceamount", "face_amount", "amount", "policyamount", "policy_amount"],
  },
  {
    key: "source",
    label: "Lead Source",
    required: false,
    description: "Where the lead came from",
    aliases: ["source", "leadsource", "lead_source", "lead source", "origin", "referral", "referralsource", "referral_source", "campaign", "campaignname", "campaign_name", "vendor", "vendorname", "vendor_name",
      // FollowUpBoss format
      "Lead Source",
      // GoHighLevel format
      "contact.source",
      // Salesforce format
      "lead: lead source", "lead:leadsource"],
  },
  {
    key: "latitude",
    label: "Latitude",
    required: false,
    description: "GPS latitude — lets Routes pre-place the lead (skips geocoding). Only used when paired with longitude.",
    aliases: ["latitude", "lat", "geolat", "geo_lat", "coord_lat", "lat_dd"],
  },
  {
    key: "longitude",
    label: "Longitude",
    required: false,
    description: "GPS longitude — lets Routes pre-place the lead (skips geocoding). Only used when paired with latitude.",
    aliases: ["longitude", "long", "lng", "lon", "geolong", "geo_long", "geo_lng", "coord_long", "long_dd"],
  },
];

const normHeader = (h: string) => h.toLowerCase().replace(/[^a-z0-9]/g, "");
const POLICY_COLUMNS = ["policynumber", "policyno", "policy", "carrier", "annualpremium", "monthlypremium", "premium"];

/**
 * In a policy file a bare "Status" column is the POLICY's status (Americo: "Active (inforce)",
 * "Lapsed"…), not the lead's stage, and the stage picked for the upload overwrites a mapped
 * stage anyway, so it was lost (Brandon Dusevic, 2026-10-06). Returns that column so it can be
 * kept as the "Policy Status" custom field; null when the file has no policy columns or
 * already has its own Policy Status column.
 */
export const policyStatusColumn = (headers: string[]): string | null => {
  if (headers.some((h) => normHeader(h) === "policystatus")) return null;
  if (!headers.some((h) => POLICY_COLUMNS.includes(normHeader(h)))) return null;
  return headers.find((h) => normHeader(h) === "status") ?? null;
};
